import {
  CheckCircle,
  Close,
  ExpandMore,
  InsertDriveFile,
  WarningAmber,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { v4 } from "uuid";
import { useAddDrawingMutation } from "../../../features/drawings";
import {
  selectUploadingFiles,
  setUploadingFiles,
} from "../../../features/drawings/drawingsSlice";
import { TooltipIconButton } from "../../shared";

function UploadDrawingsModal() {
  const { idProject } = useParams();
  const { t: tDrawings } = useTranslation("drawings");
  const { t: tGeneral } = useTranslation("general");
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errorFiles, setErrorFiles] = useState([]);
  const uploadingDrawings = useSelector(selectUploadingFiles);

  const [createDrawing] = useAddDrawingMutation();

  const dispatch = useDispatch();

  useEffect(() => {
    if (uploadingDrawings) {
      const newFiles = [...uploadingDrawings];
      setFiles((prev) => [
        ...prev.map((f) => ({ ...f, processed: true })),
        ...newFiles.map((file) => ({
          file,
          id: v4(),
        })),
      ]);
      dispatch(setUploadingFiles(null));
    }
  }, [uploadingDrawings, dispatch, setFiles]);

  const startUpload = useCallback(async () => {
    const newFiles = [...files].filter((f) => !f.processed);
    for (const file of newFiles) {
      const formData = new FormData();
      formData.append("files", file.file);
      formData.append("idProject", idProject);
      const res = await createDrawing(formData);
      if (res.error) {
        setErrorFiles((prev) => [...prev, file.id]);
      } else {
        setUploadedFiles((prev) => [...prev, file.id]);
      }
    }
  }, [files, setUploadedFiles, createDrawing, idProject, setErrorFiles]);

  useEffect(() => {
    if (files.length) {
      startUpload();
    }
  }, [files, startUpload]);

  const handleClose = (e) => {
    e.stopPropagation();
    setFiles([]);
  };

  const allUploaded = useMemo(
    () =>
      files.every(
        (file) =>
          uploadedFiles.indexOf(file.id) > -1 ||
          errorFiles.indexOf(file.id) > -1,
      ),
    [files, uploadedFiles, errorFiles],
  );

  const getStatusIcon = (id) => {
    if (uploadedFiles.indexOf(id) > -1) {
      return <CheckCircle sx={{ color: "green" }} />;
    }
    if (errorFiles.indexOf(id) > -1) {
      return <WarningAmber sx={{ color: "#FFC62B" }} />;
    }
    return <CircularProgress size={15} />;
  };

  if (files.length === 0) return null;

  return (
    <Accordion
      sx={{
        position: "absolute",
        bottom: 0,
        right: 0,
        width: "400px",
        borderTopLeftRadius: "1rem",
        borderTopRightRadius: "1rem",
      }}
      defaultExpanded
    >
      <AccordionSummary
        sx={{
          backgroundColor: "#F5F5F5",
          borderTopLeftRadius: "1rem",
          borderTopRightRadius: "1rem",
        }}
        expandIcon={<ExpandMore />}
      >
        <Box display="flex" alignItems="center" width="100%">
          <Typography>
            {tDrawings("uploadingQtyDrawings", {
              qty: files.length,
            })}
          </Typography>
          <Box flex={1} />
          <TooltipIconButton
            icon={<Close />}
            label={tGeneral("close")}
            onClick={handleClose}
            disabled={!allUploaded}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          display="flex"
          flexDirection="column"
          maxHeight="300px"
          sx={{ overflowY: "auto", overflowX: "hidden" }}
          rowGap="0.5rem"
        >
          {files.map((file) => (
            <Box
              display="flex"
              alignItems="center"
              height="1.5rem"
              columnGap="0.5rem"
              p="0.25rem 1rem"
            >
              <InsertDriveFile sx={{ color: "#E6B60C" }} />
              <Tooltip title={file.file.name}>
                <Typography
                  noWrap
                  sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  {file.file.name}
                </Typography>
              </Tooltip>
              {getStatusIcon(file.id)}
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export { UploadDrawingsModal };
